/**
 * Search identity and position for product impressions and views.
 *
 * Every impression and view is attributed to a page and section. To tell whether a
 * ranking change helped, or whether a listing only did well because it was shown
 * first, the backend also needs to know which search showed it and at what
 * position. Search responses are cached and shared between visitors, so both are
 * derived here in the browser rather than returned by the server.
 */

const newId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    // crypto.randomUUID needs a secure context (https or localhost)
    return `${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 12)}`;
  }
};

let last: { key: string; id: string } | undefined;

/**
 * One id per search: a query plus its filters and sort. Pages of the same search
 * share the id, a new query or filter gets a new one. Remembered at module level
 * so the id survives the results list remounting when the page changes. On the
 * server nothing is remembered, since that state would be shared between visitors.
 */
export const getSearchId = (key: string) => {
  if (typeof window === 'undefined') return newId();
  if (last?.key === key) return last.id;
  last = { key, id: newId() };
  return last.id;
};

/** The search a URL represents. The page number is left out on purpose. */
export const searchKey = (
  pathname: string | null | undefined,
  searchParams: { toString: () => string } | null | undefined
) => {
  const params = new URLSearchParams(searchParams?.toString() ?? '');
  params.delete('page');
  params.sort();
  return `${pathname ?? ''}?${params.toString()}`;
};

/** 0-based position in the full result list, across pages. */
export const rankOf = (page: number, itemsPerPage: number, index: number) =>
  (Math.max(page, 1) - 1) * Math.max(itemsPerPage, 0) + index;
